import { Component, Inject, OnDestroy, OnInit, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MatDateRangePicker } from '@angular/material/datepicker';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TranslocoService } from '@ngneat/transloco';
import { format } from 'date-fns';
import { cloneDeep } from 'lodash-es';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { EventsData, LogData } from '../../modules/admin/mission-users/mission-users.types';
import { CompanyUser } from '../../core/user/companyUser.model';
import { FuseConfirmationService } from '../../../@fuse/services/confirmation/confirmation.service';
import { MissionUsersService } from '../../modules/admin/mission-users/mission-users.service';

@Component({
    selector: 'app-mission-users-events-dialog',
    templateUrl: './mission-users-events-dialog.component.html',
    styles: [
        /* language=SCSS */
        `
        .events-grid {
            grid-template-columns: auto 100px 175px 250px 100px 100px 100px;

            @screen sm {
                grid-template-columns: auto 100px 175px 250px 100px 100px 100px;
            }

            @screen md {
                grid-template-columns: auto 100px 175px 250px 100px 100px 100px;
            }

            @screen lg {
                grid-template-columns:auto 100px 175px 250px 100px 100px  100px;
            }
            @screen xl {
                grid-template-columns: auto 100px 175px 250px 100px 100px 100px;
            }
            @screen 2xl {
                grid-template-columns: auto 100px 175px 250px 100px 100px 100px;
            }
            @screen 3xl {
                grid-template-columns: auto 100px 175px 250px 100px 100px 100px;
            }
        }

        `
    ],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class MissionUsersEventsDialogComponent implements OnInit, OnDestroy {

    hasChanges = false;
    dataControl: FormControl = new FormControl<string>('');
    searchControl: FormControl = new FormControl<string>(null);
    today: Date;
    date: FormControl = new FormControl<string>('', Validators.required);
    eventsData: EventsData;
    originalEventsData: EventsData;
    showData: boolean = false;
    showTextData: boolean = true;
    showRequest: boolean = false;
    showInnerResponse: boolean = false;
    dataToPrint: unknown;
    errorsOnly: boolean = false;

    private _unsubscribeAll = new Subject<void>();

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: {
            user: CompanyUser;
        },
        public dialogRef: MatDialogRef<MissionUsersEventsDialogComponent>,
        private _fuseConfirmationService: FuseConfirmationService,
        private transloco: TranslocoService,
        private missionUsersService: MissionUsersService    ) { }

    async ngOnInit() {
        this.today = new Date();
        this.date.setValue(format(this.today, 'yyyy-MM-dd'), { emitEvent: false });
        this.reloadEventsData(this.date.value);
        this.date.valueChanges.pipe(takeUntil(this._unsubscribeAll)).subscribe((date) => {
            if (date instanceof Date) {
                this.date.setValue(format(date, 'yyyy-MM-dd'), { emitEvent: false });
            }
            this.showData = false;
            this.reloadEventsData(this.date.value);
        });
        this.searchControl.valueChanges
        .pipe(
            takeUntil(this._unsubscribeAll)
        ).subscribe((value) => {
            this.createEventsData();
            });
    }

    ngOnDestroy() {
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    openDatepickerOnClick(datepicker: MatDateRangePicker<Date>) {
        if (!datepicker.opened) {
            datepicker.open();
        }
    }

    filterValues(value: string) {
        this.eventsData.logs = cloneDeep(this.originalEventsData?.logs?.filter(l => l.requestedUrl.toLowerCase().includes(value.toLowerCase())));
    }

    async showResponseEvent(event: LogData) {
        this.dataToPrint= await firstValueFrom(this.missionUsersService.retrieveUserDailyLogData(event.dailyLogKey, this.date.value, 'RESPONSE'));
        this.dataControl.setValue(JSON.stringify(this.dataToPrint, undefined, 4));
        this.showData = true;
    }

    async showRequestEvent(event: LogData) {
        this.dataToPrint = await firstValueFrom(this.missionUsersService.retrieveUserDailyLogData(event.dailyLogKey, this.date.value, 'REQUEST'));
        this.dataControl.setValue(JSON.stringify(this.dataToPrint, undefined, 4));
        this.showData = true;
    }

    async showInnerResponseEvent(event: LogData) {
        this.dataToPrint = await firstValueFrom(this.missionUsersService.retrieveUserDailyLogData(event.dailyLogKey, this.date.value, 'INNER_RESPONSE'));
        this.dataControl.setValue(JSON.stringify(this.dataToPrint, undefined, 4));
        this.showData = true;
    }

    isEmpty(obj: unknown) {
        return Object.keys(obj).length === 0;
    }

    goBack() {
        this.showData = false;
        this.showTextData = true;
    }

    logHasError(event: LogData) {
        return event.responseCode > 200;
    }

    toggleErrorsOnly() {
        this.errorsOnly = !this.errorsOnly;
        this.createEventsData();
    }

    createEventsData() {
        this.eventsData = cloneDeep(this.originalEventsData);
        if (this.searchControl.value) {
            this.filterValues(this.searchControl.value);
        }
        if (this.errorsOnly) {
            this.eventsData.logs = cloneDeep(this.eventsData.logs.filter(l => l.responseCode > 200));
        } else {
            this.eventsData.logs = cloneDeep(this.eventsData.logs);
        }
    }

    async reloadEventsData(date: string) {
        if (typeof date === 'string') {
            this.originalEventsData = await firstValueFrom(this.missionUsersService.retrieveUserDailyLog(this.data.user.userId, date));
            this.eventsData = cloneDeep(this.originalEventsData);
            this.createEventsData();
        }
    }

    async closeDialog(result?: any) {
        if (this.hasChanges) {
            const confirm = await this.confirm(null, null, this.transloco.translate('exitWithoutSaving'));
            if (confirm) {
                this.dialogRef.close(result);
            }
        } else {
            this.dialogRef.close(result);
        }
    }

    private confirm(title?: string, message?: string, confirm?: string, cancel?: string) {
        const dialogRef = this._fuseConfirmationService.open({
            title: title ? title : this.transloco.translate('unsavedChanges'),
            message: message ? message : this.transloco.translate('exitWithUnsavedChanges'),
            actions: {
                confirm: {
                    label: confirm ? confirm : this.transloco.translate('accept')
                },
                cancel: {
                    label: cancel ? cancel : this.transloco.translate('cancel')
                }
            }
        });

        return new Promise<boolean>((resolve) => {
            dialogRef.afterClosed().subscribe(result => resolve(result === 'confirmed'));
        });
    }

}
